<template>
  <div class="detail-timeline" data-testid="detail-timeline">
    <p v-if="hasStops && model.summary.text" class="timeline-summary" data-testid="timeline-summary">{{ model.summary.text }}</p>

    <!-- Viator disclosure: once, at the top, only while a tour link is on screen.
         CP-211: while the excursions fetch is in flight the same paragraph is laid out
         with hidden text, so its real height is already reserved and the timeline does
         not jump when the disclosure appears. It collapses only if no tour link shows. -->
    <p
      v-if="showDisclosure || reserveDisclosure"
      class="viator-disclosure"
      :class="{ 'viator-disclosure--reserved': !showDisclosure }"
      :aria-hidden="showDisclosure ? null : 'true'"
      data-testid="timeline-disclosure"
    >
      We may earn a commission if you book these experiences on Viator.
      <router-link to="/disclosure" class="viator-disclosure-link">Affiliate disclosure</router-link>
    </p>

    <ol v-if="hasStops" class="timeline-stops">
      <li
        v-for="stop in model.stops"
        :key="stop.key"
        class="timeline-stop"
        :class="`timeline-stop--${stop.kind}`"
        data-testid="timeline-stop"
      >
        <div class="stop-rail" aria-hidden="true">
          <span class="stop-dot">{{ stop.dayNumber }}</span>
        </div>
        <div class="stop-card">
          <div class="stop-head">
            <span class="stop-tile" aria-hidden="true">
              <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <template v-if="stop.kind === 'sea'">
                  <path d="M6 15c3-3 6-3 9 0s6 3 9 0 6-3 10 0" />
                  <path d="M6 22c3-3 6-3 9 0s6 3 9 0 6-3 10 0" />
                  <path d="M6 29c3-3 6-3 9 0s6 3 9 0 6-3 10 0" />
                </template>
                <template v-else-if="stop.kind === 'private-island'">
                  <path d="M17 31V18" />
                  <path d="M17 18c-4-1-7 1-8 4M17 18c4-1 7 1 8 4M17 18c-2-4-6-5-9-3M17 18c2-4 6-5 9-3" />
                  <circle cx="30" cy="10" r="3" />
                  <path d="M6 34c3-2 6-2 9 0s6 2 9 0 6-2 10 0" />
                </template>
                <template v-else-if="stop.kind === 'embark' || stop.kind === 'debark'">
                  <path d="M7 24h26l-4 7H11z" />
                  <path d="M13 24v-6h14v6" />
                  <path d="M18 18v-4h5v4" />
                </template>
                <template v-else>
                  <circle cx="20" cy="17" r="5" />
                  <path d="M20 6v3M20 25v3M9 17h3M28 17h3M12 9l2 2M26 23l2 2M28 9l-2 2M14 23l-2 2" />
                  <path d="M6 34c3-2 6-2 9 0s6 2 9 0 6-2 10 0" />
                </template>
              </svg>
            </span>
            <div class="stop-title">
              <div class="stop-day">
                <span class="stop-day-label">{{ stop.dayLabel }}</span>
                <span v-if="stop.dateLabel" class="stop-day-date"> &middot; {{ stop.dateLabel }}</span>
              </div>
              <h3 class="stop-port">{{ stop.title }}</h3>
            </div>
            <div v-if="stop.arrive || stop.depart" class="stop-times">
              <span v-if="stop.arrive" class="stop-chip">Arrive {{ stop.arrive }}</span>
              <span v-if="stop.depart" class="stop-chip">Depart {{ stop.depart }}</span>
            </div>
          </div>

          <p v-if="stop.description" class="stop-description">{{ stop.description }}</p>

          <div v-if="stop.kind === 'private-island'" class="stop-island">
            <p v-if="stop.note" class="stop-island-note" data-testid="excursion-stop-note">{{ stop.note }}</p>
          </div>

          <div v-else-if="stop.kind !== 'sea' && (stop.note || stop.tours.length || showTourSkeleton)" class="stop-excursions">
            <p v-if="stop.note" class="stop-note" data-testid="excursion-stop-note">{{ stop.note }}</p>
            <template v-if="stop.tours.length">
              <p class="stop-tours-label">Top tours &middot; Viator</p>
              <ul class="stop-tours">
                <tour-row v-for="tour in stop.tours" :key="tour.key" :tour="tour" />
              </ul>
              <a v-affiliate-link
                v-if="stop.viatorUrl"
                :href="stop.viatorUrl"
                target="_blank"
                rel="noopener sponsored"
                class="stop-see-all"
                data-testid="timeline-see-all"
              >{{ stop.seeAllLabel }} <span aria-hidden="true">&#8599;</span></a>
            </template>
            <!-- Reserve the tour block while the one excursions fetch is in flight. -->
            <div v-else-if="showTourSkeleton" class="stop-tours-skeleton" aria-hidden="true"></div>
          </div>
        </div>
      </li>
    </ol>

    <!-- CP-130: title already names ports; no day numbers / sea days / times. -->
    <ul v-else-if="titlePorts.length" class="itinerary-title-ports">
      <li v-for="(port, index) in titlePorts" :key="index" class="itinerary-title-port">{{ displayPort(port) }}</li>
    </ul>

    <p v-if="templateNote" class="itinerary-template-note">{{ templateNote }}</p>
    <p v-if="titleFallbackNote" class="itinerary-title-fallback-note">{{ titleFallbackNote }}</p>
    <p v-if="emptyMessage" class="itinerary-unavailable">{{ emptyMessage }}</p>
  </div>
</template>

<script>
import { computed } from 'vue';
import TourRow from './TourRow.vue';
import { displayPortName } from '@/utils/detailTimeline';

/**
 * CP-160 / CP-214: the single Itinerary timeline on Detail. Stops render from
 * the schedule immediately; tours fill in from the one excursions payload that
 * CruiseDetail already fetched. Nothing here waits on scroll or an observer.
 */
export default {
  name: 'DetailTimeline',
  components: { TourRow },
  props: {
    model: { type: Object, required: true },
    titlePorts: { type: Array, default: () => [] },
    templateNote: { type: String, default: '' },
    titleFallbackNote: { type: String, default: '' },
    emptyMessage: { type: String, default: '' },
    // Render-driven (CruiseDetail.itineraryHasExcursionSuggestions): true only while a tour link is on screen.
    showDisclosure: { type: Boolean, default: false },
    excursionsLoading: { type: Boolean, default: false }
  },
  setup(props) {
    const hasStops = computed(() => props.model.stops.length > 0);
    const showTourSkeleton = computed(() => props.excursionsLoading);
    // CP-211: reserve the disclosure line only while tours may still arrive for a stop that can have them.
    const reserveDisclosure = computed(
      () => props.excursionsLoading && props.model.stops.some((stop) => stop.kind !== 'sea')
    );
    return {
      hasStops,
      showTourSkeleton,
      reserveDisclosure,
      displayPort: displayPortName
    };
  }
};
</script>

<style scoped>
.detail-timeline {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.timeline-summary {
  margin: 0 0 var(--space-2);
  font-size: var(--text-small);
  color: var(--color-text-muted);
}

.viator-disclosure {
  margin: 0 0 var(--space-4);
  font-size: var(--text-small);
  color: var(--color-text-muted);
}

/* CP-211: laid out but invisible and unfocusable while it only reserves its height. */
.viator-disclosure--reserved {
  visibility: hidden;
}

.viator-disclosure-link {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.timeline-stops {
  list-style: none;
  margin: 0;
  padding: 0;
}

.timeline-stop {
  display: flex;
  gap: 14px;
  min-width: 0;
}

.stop-rail {
  position: relative;
  flex: none;
  width: 36px;
  display: flex;
  justify-content: center;
}

.stop-rail::after {
  content: '';
  position: absolute;
  top: 40px;
  bottom: 0;
  width: 2px;
  background: var(--color-border);
}

.timeline-stop:last-child .stop-rail::after {
  display: none;
}

.stop-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--cs-fill);
  color: #fff;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 16px;
}

.timeline-stop--sea .stop-dot {
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 2px solid var(--color-border);
}

.stop-card {
  flex: 1;
  min-width: 0;
  margin-bottom: var(--space-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 14px;
}

.timeline-stop:last-child .stop-card {
  margin-bottom: 0;
}

.timeline-stop--sea .stop-card {
  background: var(--color-background);
  border-style: dashed;
}

.stop-head {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
}

.timeline-stop--sea .stop-head {
  padding: 10px 16px;
}

.stop-tile {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.85);
  background: linear-gradient(160deg, var(--cs-signal-deep, #083f45), var(--cs-fill));
}

.stop-tile svg {
  width: 34px;
  height: 34px;
}

.timeline-stop--port .stop-tile {
  background: linear-gradient(160deg, #c45c26, #e07f3c);
}

.timeline-stop--private-island .stop-tile {
  background: linear-gradient(160deg, #18a9a3, #2fcfc3);
}

.timeline-stop--sea .stop-tile {
  width: 40px;
  height: 40px;
  background: var(--color-border-light);
  color: var(--color-text-muted);
}

.timeline-stop--sea .stop-tile svg {
  width: 26px;
  height: 26px;
}

.stop-title {
  flex: 1;
  min-width: 0;
}

.stop-day {
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.stop-port {
  margin: 1px 0 0;
  font-family: var(--font-heading);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--color-text);
}

.timeline-stop--sea .stop-port {
  font-size: 17px;
  font-weight: 500;
  color: var(--color-text-muted);
}

.stop-times {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.stop-chip {
  padding: 4px 11px;
  border: 1px solid var(--cs-signal-mist, #e8f3f4);
  border-radius: 999px;
  background: var(--color-primary-light);
  color: var(--color-primary);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.stop-description {
  margin: 0;
  padding: 0 16px 12px;
  line-height: 1.5;
  color: var(--color-text-muted);
}

.stop-excursions,
.stop-island {
  padding: 12px 16px 14px;
  border-top: 1px solid var(--color-border-light);
}

.stop-note {
  margin: 0 0 8px;
  font-size: 12.5px;
  font-style: italic;
  line-height: 1.4;
  color: var(--color-text-muted);
}

.stop-island-note {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--color-primary-light);
  font-size: 14px;
  color: var(--color-text);
}

.stop-tours-label {
  margin: 0 0 4px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.stop-tours {
  list-style: none;
  margin: 0;
  padding: 0;
}

.stop-see-all {
  display: inline-block;
  margin-top: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--color-primary);
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* CP-211: the height of a full block (label + 3 tour rows + See all), so tours arriving do not
   push the page. Desktop 157px; phone rows are 44px, so 181px. The first and last stop
   usually also carry the 3-hour boarding / disembark note, so they reserve that line too. */
.stop-tours-skeleton {
  min-height: 157px;
  border-radius: 8px;
  background: linear-gradient(90deg, var(--color-border-light), var(--color-background), var(--color-border-light));
}

.timeline-stop:first-child .stop-tours-skeleton,
.timeline-stop:last-child .stop-tours-skeleton {
  min-height: 200px;
}

.itinerary-unavailable {
  margin: 0;
  line-height: var(--line-height-body);
  color: var(--color-text-muted);
}

.itinerary-template-note,
.itinerary-title-fallback-note {
  margin: var(--space-3) 0 0;
  line-height: var(--line-height-body);
  color: var(--color-text-muted);
  font-size: var(--text-small);
  overflow-wrap: anywhere;
}

.itinerary-title-ports {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  max-width: 100%;
}

.itinerary-title-port {
  margin: 0;
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--color-border);
  font-weight: 600;
  line-height: var(--line-height-body);
  overflow-wrap: anywhere;
}

.itinerary-title-port:last-child {
  border-bottom: none;
}

@media (max-width: 767px) {
  .timeline-stop {
    gap: 10px;
  }

  .stop-rail,
  .stop-dot {
    width: 30px;
  }

  .stop-dot {
    height: 30px;
    font-size: 14px;
  }

  .stop-rail::after {
    top: 34px;
  }

  .stop-head {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px;
  }

  .stop-tile {
    width: 44px;
    height: 44px;
  }

  .stop-times {
    flex-basis: 100%;
    justify-content: flex-start;
  }

  .stop-excursions,
  .stop-island {
    padding: 10px 12px 12px;
  }

  .stop-tours-skeleton {
    min-height: 181px;
  }

  .timeline-stop:first-child .stop-tours-skeleton,
  .timeline-stop:last-child .stop-tours-skeleton {
    min-height: 242px;
  }

  .stop-port {
    font-size: 18px;
  }
}
</style>
